(() => {
  'use strict';

  const CAROUSEL_EVENTS = ['reInit', 'select', 'settle'];
  const DEFAULT_DESKTOP_BREAKPOINT = 768;
  const EMBLA_WAIT_ATTEMPTS = 100;
  const EMBLA_WAIT_INTERVAL = 50;

  const wait = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  class GlobalHeroVideoLoader extends HTMLElement {
    constructor() {
      super();
      this.handleCarouselChange = this.updateActiveVideo.bind(this);
      this.handleViewportChange = this.updateActiveVideo.bind(this);
      this.handleWindowLoad = this.scheduleInitialVideo.bind(this);
    }

    connectedCallback() {
      this.connectionId = (this.connectionId || 0) + 1;
      this.readyToLoad = false;
      this.root = this.parentElement;
      const configuredBreakpoint = Number.parseInt(
        this.dataset.desktopBreakpoint,
        10
      );
      const desktopBreakpoint =
        Number.isFinite(configuredBreakpoint) && configuredBreakpoint > 0
          ? configuredBreakpoint
          : DEFAULT_DESKTOP_BREAKPOINT;
      this.mediaQuery = window.matchMedia(
        `(min-width: ${desktopBreakpoint}px)`
      );
      this.initialise(this.connectionId);
    }

    disconnectedCallback() {
      this.connectionId = (this.connectionId || 0) + 1;
      this.removeListeners();
      this.cancelScheduledLoad();
      this.readyToLoad = false;
      this.root = null;
    }

    async initialise(connectionId) {
      const componentId = this.dataset.compId;
      if (!componentId || !this.root) return;

      const carouselTagName = `global-carousel-${componentId}`;
      await customElements.whenDefined(carouselTagName);
      if (!this.isCurrentConnection(connectionId)) return;

      this.carousel = this.root.querySelector(carouselTagName);
      if (!this.carousel) return;

      for (
        let attempt = 0;
        attempt < EMBLA_WAIT_ATTEMPTS && !this.carousel.embla;
        attempt += 1
      ) {
        await wait(EMBLA_WAIT_INTERVAL);
        if (!this.isCurrentConnection(connectionId)) return;
      }

      if (!this.carousel.embla || !this.isCurrentConnection(connectionId)) return;

      this.embla = this.carousel.embla;
      CAROUSEL_EVENTS.forEach((eventName) =>
        this.embla.on(eventName, this.handleCarouselChange)
      );

      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.handleViewportChange);
      } else {
        this.mediaQuery.addListener(this.handleViewportChange);
      }

      if (document.readyState === 'complete') {
        this.scheduleInitialVideo();
      } else {
        window.addEventListener('load', this.handleWindowLoad, { once: true });
      }
    }

    isCurrentConnection(connectionId) {
      return this.isConnected && connectionId === this.connectionId;
    }

    scheduleInitialVideo() {
      if (this.readyToLoad || this.scheduledLoadId != null) return;

      const activateVideo = () => {
        this.scheduledLoadId = null;
        this.scheduledLoadType = null;
        if (!this.isConnected) return;

        this.readyToLoad = true;
        this.updateActiveVideo();
      };

      if (window.requestIdleCallback) {
        this.scheduledLoadType = 'idle';
        this.scheduledLoadId = window.requestIdleCallback(activateVideo, {
          timeout: 1500,
        });
      } else {
        this.scheduledLoadType = 'timeout';
        this.scheduledLoadId = window.setTimeout(activateVideo, 0);
      }
    }

    updateActiveVideo() {
      if (!this.readyToLoad || !this.embla || !this.root) return;

      const slides = this.embla.slideNodes();
      const activeSlide = slides[this.embla.selectedScrollSnap()];
      const variantSelector = this.mediaQuery.matches
        ? '.video-container__desktop-video'
        : '.video-container__mobile-video';
      const activeVideo = activeSlide?.querySelector(
        `${variantSelector} video[data-deferred-video]`
      );

      this.root.querySelectorAll('video[data-deferred-video]').forEach((video) => {
        if (video !== activeVideo) video.pause();
      });

      if (activeVideo) this.hydrateVideo(activeVideo);
    }

    hydrateVideo(video) {
      const shouldAutoplay = video.dataset.autoplay === 'true';

      if (video.dataset.heroVideoLoaded !== 'true') {
        let hasSource = false;

        video.querySelectorAll('source[data-src]').forEach((source) => {
          if (!source.dataset.src) return;

          source.src = source.dataset.src;
          hasSource = true;
        });

        if (!hasSource) return;

        video.dataset.heroVideoLoaded = 'true';
        video.preload = 'auto';
        if (video.hasAttribute('muted')) video.muted = true;

        if (shouldAutoplay) {
          video.autoplay = true;
          video.setAttribute('autoplay', '');
        }

        video.load();
      }

      if (shouldAutoplay) {
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      }
    }

    removeListeners() {
      window.removeEventListener('load', this.handleWindowLoad);

      if (this.embla?.off) {
        CAROUSEL_EVENTS.forEach((eventName) =>
          this.embla.off(eventName, this.handleCarouselChange)
        );
      }

      if (this.mediaQuery?.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.handleViewportChange);
      } else if (this.mediaQuery?.removeListener) {
        this.mediaQuery.removeListener(this.handleViewportChange);
      }

      this.embla = null;
      this.carousel = null;
    }

    cancelScheduledLoad() {
      if (this.scheduledLoadId == null) return;

      if (this.scheduledLoadType === 'idle' && window.cancelIdleCallback) {
        window.cancelIdleCallback(this.scheduledLoadId);
      } else {
        window.clearTimeout(this.scheduledLoadId);
      }

      this.scheduledLoadId = null;
      this.scheduledLoadType = null;
    }
  }

  if (!customElements.get('global-hero-video-loader')) {
    customElements.define('global-hero-video-loader', GlobalHeroVideoLoader);
  }
})();
